import { css } from '@emotion/react';

/**
 * Converts pixel size to rem and accepts the base as second argument. default base is 16px
 *
 * @param {number|string} px
 * @param {number} base
 * @return {string}
 */
export const pxToRem = (px, base = 16) => {
  let tempPx = px;
  if (typeof px === 'string' || px instanceof String)
    tempPx = tempPx.replace('px', '');

  tempPx = parseInt(tempPx);
  return `${(1 / base) * tempPx}rem`;
};

/**
 * Applies RTL-aware translateX transform
 * In LTR mode, positive values are negated to maintain consistent directional behavior
 *
 * @param {string} value - The translation value (e.g., '10px')
 * @return {SerializedStyles} Emotion CSS object with transform
 */
export const translateX = (value) => {
  return document.dir === 'rtl'
    ? css`
        transform: translateX(${value});
      `
    : css`
        transform: translateX(
          ${parseInt(value) > 0
            ? -parseInt(value) + value.replace(/\d/g, '')
            : value}
        );
      `;
};

/**
 * Applies margin to the right side in RTL mode, left side in LTR mode
 *
 * @param {string} value - The margin value (e.g., '10px', '1rem')
 * @return {SerializedStyles} Emotion CSS object with appropriate margin
 */
export const marginRight = (value) => {
  return document.dir === 'rtl'
    ? css`
        margin-right: ${value};
      `
    : css`
        margin-left: ${value};
      `;
};

/**
 * Applies margin to the left side in RTL mode, right side in LTR mode
 *
 * @param {string} value - The margin value (e.g., '10px', '1rem')
 * @return {SerializedStyles} Emotion CSS object with appropriate margin
 */
export const marginLeft = (value) => {
  return document.dir === 'rtl'
    ? css`
        margin-left: ${value};
      `
    : css`
        margin-right: ${value};
      `;
};

/**
 * Applies padding to the right side in RTL mode, left side in LTR mode
 *
 * @param {string} value - The padding value (e.g., '10px', '1rem')
 * @return {SerializedStyles} Emotion CSS object with appropriate padding
 */
export const paddingRight = (value) => {
  return document.dir === 'rtl'
    ? css`
        padding-right: ${value};
      `
    : css`
        padding-left: ${value};
      `;
};

/**
 * Applies padding to the left side in RTL mode, right side in LTR mode
 *
 * @param {string} value - The padding value (e.g., '10px', '1rem')
 * @return {SerializedStyles} Emotion CSS object with appropriate padding
 */
export const paddingLeft = (value) => {
  return document.dir === 'rtl'
    ? css`
        padding-left: ${value};
      `
    : css`
        padding-right: ${value};
      `;
};

/**
 * Applies RTL-aware text alignment
 * Swaps left/right alignment based on document direction
 *
 * @param {string} value - Text alignment value ('left' or 'right')
 * @return {SerializedStyles|undefined} Emotion CSS object with text alignment, or undefined for other values
 */
export const textAlign = (value) => {
  if (String(value) === 'right') {
    return document.dir === 'rtl'
      ? css`
          text-align: right;
        `
      : css`
          text-align: left;
        `;
  }

  if (String(value) === 'left') {
    return document.dir === 'rtl'
      ? css`
          text-align: left;
        `
      : css`
          text-align: right;
        `;
  }
};

/**
 * Applies border to the right side in RTL mode, left side in LTR mode
 *
 * @param {string} value - The border value (e.g., '1px solid #ccc')
 * @return {SerializedStyles} Emotion CSS object with appropriate border
 */
export const borderRight = (value) => {
  return document.dir === 'rtl'
    ? css`
        border-right: ${value};
      `
    : css`
        border-left: ${value};
      `;
};

/**
 * Applies border to the left side in RTL mode, right side in LTR mode
 *
 * @param {string} value - The border value (e.g., '1px solid #ccc')
 * @return {SerializedStyles} Emotion CSS object with appropriate border
 */
export const borderLeft = (value) => {
  return document.dir === 'rtl'
    ? css`
        border-left: ${value};
      `
    : css`
        border-right: ${value};
      `;
};

/**
 * Applies right positioning in RTL mode, left positioning in LTR mode
 *
 * @param {string} value - The position value (e.g., '10px', '0')
 * @return {SerializedStyles} Emotion CSS object with appropriate positioning
 */
export const right = (value) => {
  return document.dir === 'rtl'
    ? css`
        right: ${value};
      `
    : css`
        left: ${value};
      `;
};

/**
 * Applies left positioning in RTL mode, right positioning in LTR mode
 *
 * @param {string} value - The position value (e.g., '10px', '0')
 * @return {SerializedStyles} Emotion CSS object with appropriate positioning
 */
export const left = (value) => {
  return document.dir === 'rtl'
    ? css`
        left: ${value};
      `
    : css`
        right: ${value};
      `;
};

/**
 * Generates custom scrollbar styles with cross-browser support
 *
 * @param {number} val - Scrollbar width in pixels (default: 4)
 * @param {string} color - Scrollbar thumb color (default: '#e4e9ec')
 * @param {string} trackColor - Scrollbar track color (default: 'white')
 * @return {SerializedStyles} Emotion CSS object with scrollbar styles
 */
export const scrollbar = (val = 4, color = '#e4e9ec', trackColor = 'white') => {
  return css`
    @supports not selector(::-webkit-scrollbar) {
      scrollbar-color: ${color} ${trackColor};
      scrollbar-width: thin;
    }

    /* width */
    &::-webkit-scrollbar {
      width: ${val}px !important;
      color: #d1d1d1 !important;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      box-shadow: inset 0 0 100px ${trackColor} !important;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: ${color} !important;
      border-radius: 4px !important;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: ${color} !important;
    }
  `;
};
